import { useEffect, useState } from 'react';
import { api, type ComedyChannel, type ComedyScenario, type ComedyScenarioDetail } from '../../lib/api';
import { Button, ErrorAlert, Spinner } from '../../components/ui';
import { ComedyEmpty, ComedySection, ComedyStatus, Field, formatMs, formatUsd } from './ComedyShared';

function riskTone(score: number): 'success' | 'warning' | 'danger' {
  if (score >= 70) return 'danger';
  if (score >= 40) return 'warning';
  return 'success';
}

function ScoreBar({ label, value, tone }: { label: string; value: number; tone?: 'success' | 'warning' | 'danger' }) {
  return (
    <div style={{ minWidth: 90 }}>
      <div className="muted" style={{ fontSize: 11 }}>{label}</div>
      <div style={{ fontWeight: 700, color: tone === 'danger' ? 'var(--danger)' : tone === 'warning' ? 'var(--warning)' : tone === 'success' ? 'var(--success)' : undefined }}>{Math.round(value)}</div>
    </div>
  );
}

function ScenarioRow({ scenario, onOpen }: { scenario: ComedyScenario; onOpen: (scenario: ComedyScenario) => void }) {
  return (
    <tr>
      <td style={{ maxWidth: 340 }}><strong>{scenario.title}</strong><div className="muted" style={{ fontSize: 11 }}>{scenario.format} · {scenario.creationMode}</div></td>
      <td><ComedyStatus value={scenario.status} /></td>
      <td>{scenario.proposedDurationSeconds} s</td>
      <td><ScoreBar label="humor" value={scenario.humorScore} /><ScoreBar label="oryginalność" value={scenario.originalityScore} /></td>
      <td><ScoreBar label="ryzyko" value={scenario.legalRiskScore} tone={riskTone(scenario.legalRiskScore)} /></td>
      <td>{Math.round(scenario.qualityScore)}</td>
      <td><Button size="sm" onClick={() => onOpen(scenario)}>Otwórz</Button></td>
    </tr>
  );
}

export function ComedyScenariosPanel({ channel, onOpenDirector }: { channel: ComedyChannel; onOpenDirector: (scenarioId?: string) => void }) {
  const [scenarios, setScenarios] = useState<ComedyScenario[]>([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [detail, setDetail] = useState<ComedyScenarioDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const load = async () => {
    setLoading(true); setError(null);
    try { setScenarios((await api.comedy.scenarios(channel.id, status || undefined)).scenarios); } catch (err) { setError(err); } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [channel.id, status]);

  const open = async (scenario: ComedyScenario) => {
    setError(null);
    try { setDetail(await api.comedy.scenario(channel.id, scenario.id)); } catch (err) { setError(err); }
  };

  const approve = async (scenario: ComedyScenario) => {
    const acknowledge = scenario.legalRiskScore >= 70 || scenario.riskFlags.length > 0
      ? window.confirm('Scenariusz ma flagi ryzyka prawnego. Potwierdzasz ręczną ocenę i zatwierdzenie?')
      : true;
    if (!acknowledge) return;
    setBusy(true); setError(null);
    try { await api.comedy.approveScenario(channel.id, scenario.id, true); setMessage('Scenariusz zatwierdzony.'); await load(); } catch (err) { setError(err); } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <ComedySection
        title="Scenariusze AI"
        description="Warianty wygenerowane przez Comedy Directora. Każda kwestia pochodzi z rzeczywistego, zatwierdzonego fragmentu biblioteki."
        actions={<div className="btn-row"><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="">wszystkie statusy</option><option value="REVIEW">REVIEW</option><option value="DRAFT">DRAFT</option><option value="APPROVED">APPROVED</option><option value="PRODUCED">PRODUCED</option><option value="REJECTED">REJECTED</option></select><Button size="sm" onClick={() => void load()}>Odśwież</Button><Button size="sm" variant="primary" onClick={() => onOpenDirector()}>Generuj w Directorze</Button></div>}
      >
        {error ? <ErrorAlert error={error} /> : null}
        {message ? <div className="alert alert--success">{message}</div> : null}
        {loading ? <Spinner label="Ładowanie scenariuszy…" /> : scenarios.length === 0 ? (
          <ComedyEmpty title="Brak scenariuszy" hint="Uruchom Comedy Director, aby wygenerować warianty z posiadanej biblioteki wypowiedzi." action={<Button variant="primary" onClick={() => onOpenDirector()}>Otwórz Comedy Director</Button>} />
        ) : (
          <div className="table-wrap" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead><tr><th>Tytuł</th><th>Status</th><th>Długość</th><th>Oceny</th><th>Ryzyko</th><th>Quality</th><th /></tr></thead>
              <tbody>
                {scenarios.map((scenario) => <ScenarioRow key={scenario.id} scenario={scenario} onOpen={(entry) => void open(entry)} />)}
              </tbody>
            </table>
          </div>
        )}
      </ComedySection>

      {detail && (
        <ScenarioDetailCard
          detail={detail}
          busy={busy}
          onClose={() => setDetail(null)}
          onApprove={() => void approve(detail.scenario)}
          onReject={async () => { setBusy(true); try { await api.comedy.rejectScenario(channel.id, detail.scenario.id); setDetail(null); await load(); } catch (err) { setError(err); } finally { setBusy(false); } }}
          onProduce={() => onOpenDirector(detail.scenario.id)}
        />
      )}
    </div>
  );
}

export function ScenarioDetailCard({ detail, busy, onApprove, onReject, onProduce, onClose }: { detail: ComedyScenarioDetail; busy: boolean; onApprove: () => void; onReject: () => void; onProduce: () => void; onClose: () => void }) {
  const scenario = detail.scenario;
  return (
    <ComedySection
      title={<span>{scenario.title} <ComedyStatus value={scenario.status} /></span>}
      description={`${scenario.format} · ${scenario.creationMode} · ${scenario.proposedDurationSeconds} s · quality ${Math.round(scenario.qualityScore)}`}
      actions={<div className="btn-row"><Button size="sm" onClick={onClose}>Zamknij</Button>{['DRAFT', 'REVIEW', 'REJECTED'].includes(scenario.status) && <><Button size="sm" variant="success" onClick={onApprove} disabled={busy}>Zatwierdź</Button><Button size="sm" variant="danger" onClick={onReject} disabled={busy}>Odrzuć</Button></>}{scenario.status === 'APPROVED' && <Button size="sm" variant="primary" onClick={onProduce}>Do produkcji</Button>}</div>}
    >
      <div className="grid grid--2">
        <div>
          <Field label="Hook"><div style={{ padding: 8, background: 'var(--surface-2)', borderRadius: 8 }}>{scenario.hook ?? '—'}</div></Field>
          <Field label="Punkt kulminacyjny"><div style={{ padding: 8, background: 'var(--surface-2)', borderRadius: 8 }}>{scenario.climax ?? '—'}</div></Field>
          <Field label="Punkt"><div style={{ padding: 8, background: 'var(--surface-2)', borderRadius: 8 }}>{scenario.punchline ?? '—'}</div></Field>
        </div>
        <div>
          <div className="inline" style={{ gap: 16, flexWrap: 'wrap' }}>
            <ScoreBar label="Humor" value={scenario.humorScore} />
            <ScoreBar label="Oryginalność" value={scenario.originalityScore} />
            <ScoreBar label="Ryzyko prawne" value={scenario.legalRiskScore} tone={riskTone(scenario.legalRiskScore)} />
            <ScoreBar label="Potencjał udostępnień" value={scenario.sharePotentialScore} />
            
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>Koszt: {formatUsd(scenario.costUsd)} · model: {scenario.modelProvider ?? '—'}/{scenario.modelId ?? '—'}</div>
          {scenario.riskFlags.length > 0 && <div className="alert alert--warning" style={{ marginTop: 10 }}>Flagi ryzyka: {scenario.riskFlags.join(', ')} — wymagana świadoma ocena administratora.</div>}
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Oś czasu montażu ({detail.beats.length} fragmentów)</div>
        {detail.beats.map((beat) => (
          <div key={beat.id} className="card" style={{ padding: 10, marginBottom: 8 }}>
            <div className="inline" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <div className="inline" style={{ gap: 8 }}>
                <span className="badge badge--info">{beat.role}</span>
                <strong>{beat.segment.speakerName ?? 'mówca'}</strong>
                <span className="muted" style={{ fontSize: 11 }}>{formatMs(beat.segment.startMs + (beat.trimStartMs ?? 0))}–{formatMs(beat.segment.startMs + (beat.trimEndMs ?? beat.segment.durationMs))}</span>
              </div>
              <span className="muted" style={{ fontSize: 11 }}>{beat.segment.sourceTitle ?? ''}</span>
            </div>
            <div style={{ marginTop: 6 }}>„{beat.scriptedLine}”</div>
            {beat.segment.attributionRequired && <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>Atrybucja: {beat.segment.attributionText ?? 'wymagana'}</div>}
          </div>
        ))}
        <div className="muted" style={{ fontSize: 12 }}>
          Materiał jest fikcyjnym montażem autentycznych wypowiedzi. Oznaczenie „PARODIA · FIKCYJNY MONTAŻ” zostanie wypalone w filmie.
        </div>
      </div>
    </ComedySection>
  );
}

export function ComedyDirectorPanel({ channel, initialScenarioId }: { channel: ComedyChannel; initialScenarioId?: string | null }) {
  const [scenarios, setScenarios] = useState<ComedyScenario[]>([]);
  const [mode, setMode] = useState<'HYBRID_AUTO' | 'STORY_FIRST' | 'FOOTAGE_FIRST'>('HYBRID_AUTO');
  const [topic, setTopic] = useState('');
  const [count, setCount] = useState(2);
  const [targetWordCount, setTargetWordCount] = useState<number | ''>('');
  const [targetClipCount, setTargetClipCount] = useState<number | ''>('');
  const [script, setScript] = useState('');
  const [detail, setDetail] = useState<ComedyScenarioDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState('');
  const [rejected, setRejected] = useState<Array<{ title: string; reasons: string[] }>>([]);
  const [ackRisk, setAckRisk] = useState(false);
  const [ideas, setIdeas] = useState<Array<{ title: string; premise: string; hook: string; format: string; searchKeywords: string[] }>>([]);
  const [ideasBusy, setIdeasBusy] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const response = await api.comedy.scenarios(channel.id);
      setScenarios(response.scenarios);
      const target = initialScenarioId ? response.scenarios.find((scenario) => scenario.id === initialScenarioId) : null;
      if (target) setDetail(await api.comedy.scenario(channel.id, target.id));
    } catch (err) { setError(err); }
  };
  useEffect(() => { void load(); }, [channel.id, initialScenarioId]);

  const generateIdeas = async () => {
    setIdeasBusy(true); setError(null);
    try {
      const result = await api.comedy.ideas(channel.id, 6);
      setIdeas(result.ideas);
      setMessage(`Zaproponowano ${result.ideas.length} tematów. Koszt: ${formatUsd(result.costUsd)}.`);
    } catch (err) { setError(err); } finally { setIdeasBusy(false); }
  };

  const generate = async () => {
    setBusy(true); setError(null); setMessage(''); setRejected([]);
    try {
      const result = await api.comedy.generateScenarios(channel.id, {
        count, mode, topic: topic || null,
        targetWordCount: targetWordCount === '' ? null : Number(targetWordCount),
        targetClipCount: targetClipCount === '' ? null : Number(targetClipCount),
        script: script.trim() || null,
      });
      setMessage(`Wygenerowano ${result.scenarios.length} wariant(y) w trybie ${result.mode}. Koszt: ${formatUsd(result.costUsd)}.`);
      setRejected(result.rejectedVariants);
      await load();
      const first = result.scenarios[0];
      if (first) setDetail(await api.comedy.scenario(channel.id, first.id));
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  const approve = async () => {
    if (!detail) return;
    setBusy(true); setError(null);
    try {
      await api.comedy.approveScenario(channel.id, detail.scenario.id, ackRisk);
      setMessage('Scenariusz zatwierdzony. Możesz uruchomić produkcję w Video Studio.');
      setAckRisk(false);
      setDetail(await api.comedy.scenario(channel.id, detail.scenario.id));
      await load();
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  const produce = async () => {
    if (!detail) return;
    setBusy(true); setError(null); setMessage('');
    try {
      const result = await api.comedy.createProduction(channel.id, detail.scenario.id);
      setMessage(result.reused ? 'Produkcja już istnieje — otwórz Video Studio.' : 'Render zakolejkowany. Postęp znajdziesz w Video Studio i Kolejce produkcji.');
      await load();
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <ComedySection
        title="AI Comedy Director"
        description="Hybrydowy reżyser: STORY FIRST wymyśla sytuację i dopasowuje prawdziwe wypowiedzi; FOOTAGE FIRST odkrywa zaskakujące zestawienia; HYBRID AUTO wybiera metodę na podstawie biblioteki."
        actions={<Button size="sm" onClick={() => void load()}>Odśwież</Button>}
      >
        {error ? <ErrorAlert error={error} /> : null}
        {message ? <div className="alert alert--success">{message}</div> : null}
        <div className="form-row">
          <Field label="Tryb tworzenia">
            <select value={mode} onChange={(event) => setMode(event.target.value as typeof mode)}>
              <option value="HYBRID_AUTO">HYBRID AUTO (domyślny)</option>
              <option value="STORY_FIRST">STORY FIRST</option>
              <option value="FOOTAGE_FIRST">FOOTAGE FIRST</option>
            </select>
          </Field>
          <Field label="Liczba wariantów"><select value={count} onChange={(event) => setCount(Number(event.target.value))}><option value={1}>1</option><option value={2}>2</option><option value={3}>3</option></select></Field>
        </div>
        <Field label="Temat / inspiracja (opcjonalnie)" hint="Story First użyje tematu; Footage First i tak wybierze najlepsze dostępne wypowiedzi."><input value={topic} onChange={(event) => setTopic(event.target.value)} placeholder="np. rozmowa kwalifikacyjna, absurd w biurze" /></Field>
        <div className="form-row">
          <Field label="Docelowo słów (opcjonalnie)" hint="Np. 20 słów. System dobierze około 1 wycinek na 2 słowa."><input type="number" min={3} max={300} value={targetWordCount} onChange={(event) => setTargetWordCount(event.target.value === '' ? '' : Number(event.target.value))} placeholder="20" /></Field>
          <Field label="Liczba materiałów / wycinków" hint="2–20. Puste = wyliczone ze słów (20 słów → 10)."><input type="number" min={2} max={20} value={targetClipCount} onChange={(event) => setTargetClipCount(event.target.value === '' ? '' : Number(event.target.value))} placeholder="10" /></Field>
        </div>
        <Field label="Mój szkic scenariusza (opcjonalnie)" hint="Wklej tekst np. 20 słów. Tryb deterministyczny: dzieli tekst na fragmenty i dopasowuje realne wypowiedzi z biblioteki. Nie cytuje szkicu jako wypowiedzi.">
          <textarea rows={3} value={script} onChange={(event) => setScript(event.target.value)} placeholder="Szef pyta o raport, nikt nic nie wie, wszyscy udają ekspertów…" />
        </Field>
        <div className="btn-row">
          <Button variant="primary" onClick={() => void generate()} disabled={busy}>{busy ? 'Generowanie…' : 'Wygeneruj scenariusze'}</Button>
          <Button onClick={() => void generateIdeas()} disabled={ideasBusy}>{ideasBusy ? 'Wymyślanie…' : 'Zaproponuj tematy AI'}</Button>
          <span className="muted" style={{ fontSize: 12 }}>Budżet kanału: {formatUsd(channel.aiBudgetDailyUsd)}/dzień</span>
        </div>
        {ideas.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Pomysły na odcinki (bez cytatów — sytuacje do researchu)</div>
            <div className="grid grid--2">
              {ideas.map((idea) => (
                <div key={idea.title} className="card" style={{ padding: 12 }}>
                  <div className="inline" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}><strong>{idea.title}</strong><span className="badge badge--info">{idea.format}</span></div>
                  <div style={{ marginTop: 6 }}>{idea.premise}</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>Hook: {idea.hook}</div>
                  <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>Szukaj: {idea.searchKeywords.join(', ')}</div>
                  <div style={{ marginTop: 8 }}><Button size="sm" onClick={() => setTopic(idea.title)}>Użyj jako temat</Button></div>
                </div>
              ))}
            </div>
          </div>
        )}
        {rejected.length > 0 && (
          <div className="alert alert--warning" style={{ marginTop: 12 }}>
            Odrzucone warianty (nieoparte na rzeczywistych wypowiedziach): {rejected.map((entry) => `${entry.title} — ${entry.reasons.slice(0, 2).join(', ')}`).join(' | ')}
          </div>
        )}
      </ComedySection>

      <ComedySection title="Ostatnie scenariusze" description="Wybierz wariant, aby zobaczyć oś czasu i zatwierdzić produkcję.">
        {scenarios.length === 0 ? <ComedyEmpty title="Brak scenariuszy" hint="Wygeneruj pierwsze warianty powyżej." /> : (
          <div className="grid grid--2">
            {scenarios.slice(0, 8).map((scenario) => (
              <button key={scenario.id} type="button" className="card" style={{ padding: 12, textAlign: 'left', cursor: 'pointer' }} onClick={() => void api.comedy.scenario(channel.id, scenario.id).then(setDetail).catch(setError)}>
                <div className="inline" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}><strong>{scenario.title}</strong><ComedyStatus value={scenario.status} /></div>
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{scenario.format} · {scenario.proposedDurationSeconds} s · quality {Math.round(scenario.qualityScore)}</div>
                <div className="muted" style={{ fontSize: 12 }}>humor {Math.round(scenario.humorScore)} · ryzyko {Math.round(scenario.legalRiskScore)}</div>
                <div style={{ marginTop: 6 }}>{scenario.hook}</div>
              </button>
            ))}
          </div>
        )}
      </ComedySection>

      {detail && (
        <>
          <ScenarioDetailCard
            detail={detail}
            busy={busy}
            onClose={() => setDetail(null)}
            onApprove={() => void approve()}
            onReject={() => void api.comedy.rejectScenario(channel.id, detail.scenario.id).then(() => { setDetail(null); return load(); }).catch(setError)}
            onProduce={() => void produce()}
          />
          {['DRAFT', 'REVIEW'].includes(detail.scenario.status) && (detail.scenario.legalRiskScore >= 70 || detail.scenario.riskFlags.length > 0) && (
            <label className="inline"><input type="checkbox" checked={ackRisk} onChange={(event) => setAckRisk(event.target.checked)} /> Potwierdzam ręczną ocenę ryzyka prawnego i akceptuję scenariusz do produkcji.</label>
          )}
        </>
      )}
    </div>
  );
}
